/**
 * The pin map of a new design (pages/Designer.tsx; pure, tested with node --test): the calculator's signed satellite
 * image (`GET /v1/orgs/{org}/site-map`, as the website widget's `/public/site-map`) is Web Mercator, centred on
 * `center`, `width` CSS px wide at `zoom`. These helpers place the pin on it and read a point back, as the widget
 * does (apps/solar-frontend/js/app.js).
 */
export type LatLng = { lat: number; lng: number };
export type MapImage = { center: LatLng; zoom: number; width: number; height: number; tile_size?: number };

const TILE = 256;

/** Web Mercator world pixels of a point at `zoom` (256 px tiles). */
export function worldPx(p: LatLng, zoom: number, tile = TILE): { x: number; y: number } {
  const size = tile * 2 ** zoom;
  const s = Math.sin((p.lat * Math.PI) / 180);
  return { x: ((p.lng + 180) / 360) * size, y: (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * size };
}

export function fromWorldPx(x: number, y: number, zoom: number, tile = TILE): LatLng {
  const size = tile * 2 ** zoom;
  const n = Math.PI - (2 * Math.PI * y) / size;
  return { lat: (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))), lng: (x / size) * 360 - 180 };
}

/** Where `p` is on the map shown `viewW` × `viewH` CSS px (may lie outside the view). */
export function pinPosition(map: MapImage, p: LatLng, viewW: number, viewH: number): { x: number; y: number } {
  const k = viewW / map.width;
  const c = worldPx(map.center, map.zoom, map.tile_size);
  const q = worldPx(p, map.zoom, map.tile_size);
  return { x: viewW / 2 + (q.x - c.x) * k, y: viewH / 2 + (q.y - c.y) * k };
}

/** The point under (`x`, `y`) CSS px of the view, clamped to the view. */
export function pointAt(map: MapImage, x: number, y: number, viewW: number, viewH: number): LatLng {
  const k = viewW / map.width;
  const px = Math.max(0, Math.min(viewW, x));
  const py = Math.max(0, Math.min(viewH, y));
  const c = worldPx(map.center, map.zoom, map.tile_size);
  return fromWorldPx(c.x + (px - viewW / 2) / k, c.y + (py - viewH / 2) / k, map.zoom, map.tile_size);
}

/** Ground distance in metres between two points (haversine). */
export function distanceM(a: LatLng, b: LatLng): number {
  const r = 6371008.8;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(h));
}

/** Whether the pin map offers zooming: only for a large building (`GET /v1/orgs/{org}/site-building`, the imagery
 * stage's 400 m² ground area), or when its size could not be known (so nobody is stuck on a crop too small). */
export function offerZoom(building: { known: boolean; large: boolean } | undefined, failed: boolean): boolean {
  if (failed) return true;
  if (!building) return false; // still looking: no controls that would vanish a moment later
  return building.large || !building.known;
}

/** The longest address the API's address suggestions take (services/public_places.py); the field stops there. */
export const ADDRESS_MAX_LENGTH = 200;

/** What the Designer asks suggestions for: the trimmed text, or null when it is too short to search or longer than
 * the API takes (a longer one used to come back as "input or session is too long", live test EX-02). */
export function addressQuery(text: string): string | null {
  const q = text.trim();
  return q.length >= 4 && q.length <= ADDRESS_MAX_LENGTH ? q : null;
}

/** "lat, lng" typed by the user (the Designer's fallback when address suggestions are paused); null if invalid. */
export function parseCoordinates(text: string): { lat: number; lng: number } | null {
  const m = text.trim().match(/^(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)$/);
  if (!m) return null;
  const lat = Number(m[1]), lng = Number(m[2]);
  return Math.abs(lat) <= 85 && Math.abs(lng) <= 180 ? { lat, lng } : null;
}
